import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import WorkOrderDetail from '../../pages/WorkOrderDetail';

export default function WorkOrderDetailSlidePanel({
  isOpen,
  onClose,
  workOrderId,
  onDataChange = null
}) {
  const [shouldRender, setShouldRender] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  useEffect(() => {
    if (isOpen && workOrderId) {
      setShouldRender(true);
      const timer = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(timer);
    }
    if (!isOpen) {
      setIsAnimating(false);
      const timer = setTimeout(() => setShouldRender(false), 440);
      return () => clearTimeout(timer);
    }
  }, [isOpen, workOrderId]);

  if (!shouldRender) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isAnimating ? 'bg-opacity-50' : 'bg-opacity-0'
        }`}
        onClick={onClose}
        aria-hidden="true"
      />

      {/* Slide Panel */}
      <div
        className={`fixed inset-y-0 right-0 z-50 w-full max-w-4xl bg-sky-950 shadow-2xl transform transition-transform duration-[440ms] ease-in-out md:max-w-5xl lg:max-w-6xl ${
          isAnimating ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ transitionTimingFunction: 'cubic-bezier(0.4, 0.0, 0.2, 1)' }}
        role="dialog"
        aria-modal="true"
        aria-label="Work order details"
      >
        {/* Top toolbar */}
        <div className="absolute top-4 left-4 z-10 flex items-center space-x-2">
          <button
            onClick={onClose}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            aria-label="Close work order details"
          >
            <svg className="w-6 h-6 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          <button
            onClick={() => window.open(`/dashboard/work-orders/${workOrderId}`, '_blank')}
            className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-white shadow-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
            aria-label="Open in new tab"
            title="Open in new tab"
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
            </svg>
          </button>
        </div>

        {/* Scrollable content */}
        <div className="h-full overflow-y-auto">
          <div className="pt-16 pb-6 px-4 sm:px-6">
            {isAnimating && workOrderId && (
              <WorkOrderDetail
                workOrderId={workOrderId}
                isSlidePanel
                onDataChange={onDataChange}
              />
            )}
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}
